import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BadgeCheck, Loader2, Sparkles, TrendingUp, Eye, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERKS = [
  { icon: Sparkles, title: "Editor's Pick badge", desc: "Your program gets the gold Editor's Pick badge across the directory and homepage." },
  { icon: TrendingUp, title: "Top-of-directory placement", desc: "Featured programs surface first in your niche and in trending lists." },
  { icon: Eye, title: "Homepage spotlight", desc: "Placement in the 'Top picks this month' section seen by every visitor." },
  { icon: ShieldCheck, title: "Verified data review", desc: "Our team verifies and refreshes your commission, EPC and payout data." },
];

export default function GetFeaturedPage() {
  const [form, setForm] = useState({ program_name: "", company: "", email: "", website: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, { ...form, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error("Could not start checkout. Please check your details and try again.");
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <main data-testid="get-featured-page" className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
        <div>
          <p className="uppercase text-xs tracking-[0.2em] font-semibold text-emerald-600 mb-2 flex items-center gap-1.5"><BadgeCheck className="w-4 h-4" /> For Program Owners</p>
          <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-4">Get your program featured.</h1>
          <p className="text-slate-600 leading-relaxed mb-10 max-w-lg">
            Put your affiliate program in front of thousands of marketers actively comparing offers. One-time payment, 30 days of featured placement.
          </p>
          <div className="space-y-5">
            {PERKS.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex gap-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 h-fit"><Icon className="w-5 h-5 text-emerald-600" /></div>
                <div>
                  <p className="font-medium text-sm">{title}</p>
                  <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-8 lg:p-10">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500">Featured Listing</p>
              <p className="font-heading text-4xl font-medium mt-1">$49 <span className="text-base text-slate-400 font-normal">one-time · 30 days</span></p>
            </div>
          </div>
          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500 block mb-2">Program name *</label>
              <input required value={form.program_name} onChange={set("program_name")} maxLength={200} placeholder="e.g. Acme Affiliate Program" data-testid="featured-program-name-input"
                className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
            </div>
            <div>
              <label className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500 block mb-2">Company</label>
              <input value={form.company} onChange={set("company")} maxLength={200} placeholder="Acme Inc." data-testid="featured-company-input"
                className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
            </div>
            <div>
              <label className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500 block mb-2">Contact email *</label>
              <input type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com" data-testid="featured-email-input"
                className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
            </div>
            <div>
              <label className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500 block mb-2">Program website *</label>
              <input required value={form.website} onChange={set("website")} maxLength={500} placeholder="https://company.com/affiliates" data-testid="featured-website-input"
                className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
            </div>
            <button type="submit" disabled={loading} data-testid="featured-checkout-button"
              className="w-full flex items-center justify-center gap-2 py-4 rounded-full bg-emerald-600 text-white font-medium hover:bg-emerald-500 transition-colors duration-200 disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BadgeCheck className="w-4 h-4" />}
              Continue to secure checkout — $49
            </button>
            <p className="text-xs text-slate-400 text-center">Payments securely processed by Stripe. Tax handled automatically at checkout.</p>
          </form>
        </div>
      </div>
    </main>
  );
}
